import * as Sentry from '@sentry/react';

export type PortalApp = 'admin-portal' | 'driver-h5' | 'client-portal';

function sentryDsn(): string | undefined {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  return typeof dsn === 'string' && dsn.trim() ? dsn.trim() : undefined;
}

export function isSentryEnabled(): boolean {
  return Boolean(sentryDsn());
}

export function initSentry(options: { app: PortalApp }): void {
  const dsn = sentryDsn();
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_APP_ENV || 'development',
    release: `${options.app}@${import.meta.env.VITE_APP_VERSION || '0.0.0'}`,
    tracesSampleRate: 0.1,
    sendDefaultPii: false,
    beforeSend(event) {
      // Never ship auth tokens or passwords if they leak into extras.
      if (event.extra) {
        for (const key of Object.keys(event.extra)) {
          const lower = key.toLowerCase();
          if (
            lower.includes('password') ||
            lower.includes('token') ||
            lower.includes('authorization') ||
            lower.includes('secret')
          ) {
            delete event.extra[key];
          }
        }
      }
      return event;
    },
  });

  Sentry.setTag('app', options.app);
}

export function captureException(
  error: unknown,
  context?: Record<string, unknown>
): void {
  if (!isSentryEnabled()) {
    console.error('[ubus]', error, context);
    return;
  }

  Sentry.withScope((scope) => {
    if (context) {
      for (const [key, value] of Object.entries(context)) {
        scope.setExtra(key, value);
      }
      if (typeof context.requestId === 'string') {
        scope.setTag('requestId', context.requestId);
      }
      if (typeof context.fn === 'string') {
        scope.setTag('fn', context.fn);
      }
    }
    Sentry.captureException(error);
  });
}

export function setSentryUser(
  user: { id: string; role?: string } | null
): void {
  if (!isSentryEnabled()) return;
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({
    id: user.id,
    ...(user.role ? { data: { role: user.role } } : {}),
  });
}

export { Sentry };
